import { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Sheet } from './Sheet';
import { ArrowRight, Plus, X } from './icons';
import { useAuth } from '../../context/AuthContext';
import { useHaptics } from '../../hooks/useHaptics';
import { useActiveLeague } from '../../hooks/useActiveLeague';
import { createMiniLeaguePath } from '../../screens/leagues/miniLeagueHelpers';
import {
  useLeagues,
  useMiniLeagues,
  useMyEntry,
  useMyMemberships,
  useProfile,
  useUpdateUserDoc
} from '../../hooks/useQueries';
import type { MiniLeaguePromoState } from '@h2h/shared';

/** One-shot, per-device fast-path: a local mirror of "seen" so the nudge never even
 *  flickers before the profile (the cross-device source of truth) has loaded. */
export const SEEN_KEY = 'h2h:miniLeaguePromoSeen';
/** Let the screen they landed on settle before the sheet rises — feels deliberate, not jumpy. */
export const SHOW_DELAY_MS = 700;

function readSeen(): boolean {
  try {
    return localStorage.getItem(SEEN_KEY) === '1';
  } catch {
    return false;
  }
}

/** Decorative avatar pile (the "mates to beat"). Letters/colours are illustrative —
 *  a brand-new player rarely has friends added yet, so we don't read real ones. */
const PILE = [
  { letter: 'F', size: 50, from: '#F4937F', to: '#E2604C' },
  { letter: 'A', size: 54, from: '#B98BE8', to: '#8E5BD6' },
  { letter: 'T', size: 60, from: '#2A2A33', to: '#0E0E12' },
  { letter: 'M', size: 54, from: '#5FB7F0', to: '#3A7FD6' },
  { letter: 'J', size: 50, from: '#5FD0A0', to: '#1DA873' }
] as const;

/**
 * The Mini League promo — a one-time bottom sheet that nudges an engaged player to
 * spin up a private table with their mates. It rides the shared {@link Sheet} (so it
 * opens, drags and dismisses exactly like every other sheet) and only ever fires at
 * a *natural* moment, when the pitch lands hardest:
 *
 *   1. account is set up (signed in + profile complete — guaranteed inside AppChrome),
 *   2. they've joined a base league (you need one to create a mini league anyway),
 *   3. they've actually made picks — their first entry this round, or any settled
 *      history — so they already "get" the game before we ask them to invite friends,
 *   4. they're not already in a mini league (nothing to promote then), and
 *   5. they haven't seen it before — tracked on the user doc (so it's once per
 *      account, not per device) and mirrored to localStorage for a flicker-free gate.
 *
 * It's deliberately quiet: shown once, skipped while picking or already in the
 * leagues section, and the permanent entry points (Friends tab / Hub) stay the home
 * for this anyway. Mounted once in AppChrome; the panel portals to #overlay-root.
 *
 * Every showing + exit (tapped Create / Join, or dismissed) is recorded on the user
 * doc, which the admin Promos page aggregates into a funnel.
 */
export function MiniLeaguePromo() {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { impact } = useHaptics();
  const { uid } = useAuth();

  const { data: profile } = useProfile(uid);
  const { data: memberships } = useMyMemberships(uid);
  const { data: miniLeagues } = useMiniLeagues(uid);
  const { leagueId } = useActiveLeague();
  const { data: leagues } = useLeagues();

  const updateUser = useUpdateUserDoc(uid);
  // The mutation object's identity changes each render; hold it in a ref so the
  // show-effect can fire the impression write without listing it as a dep (which
  // would reset the reveal timer on every re-render).
  const updateUserRef = useRef(updateUser);
  updateUserRef.current = updateUser;

  const [seen, setSeen] = useState(readSeen);
  const [open, setOpen] = useState(false);
  const openedRef = useRef(false);
  // The shownAt stamped when the sheet rose, so the exit write reuses the same
  // timestamp; plus a one-shot guard so only the first exit is recorded.
  const shownAtRef = useRef<number | null>(null);
  const outcomeRef = useRef(false);

  const hasMiniLeague = (miniLeagues?.length ?? 0) > 0;
  // Once per ACCOUNT: the user doc is the source of truth (so it won't re-pop on a
  // second device), with localStorage as a flicker-free local mirror. Either having
  // fired suppresses the nudge.
  const alreadyShown = seen || !!profile?.miniLeaguePromo?.shownAt;
  // Don't even subscribe to the (live) entry doc unless this player is still a
  // candidate — keeps the Firestore listener off for everyone who's done with it.
  const consider = !!uid && !alreadyShown && !hasMiniLeague;

  const activeLeague = leagues?.find((l) => l.leagueId === leagueId);
  const roundId = activeLeague?.currentRoundId ?? null;
  const { data: entry } = useMyEntry(
    consider ? leagueId ?? undefined : undefined,
    consider ? roundId : null,
    consider ? uid : null
  );

  // "Has made picks": an entry for the current round (the just-submitted moment) or
  // any prior play recorded on a membership (a returning, engaged player).
  const playedBefore = (memberships ?? []).some(
    (m) => (m.roundsPlayed ?? 0) > 0 || (m.seasonPoints ?? 0) > 0
  );
  const hasPicked = !!entry || playedBefore;

  // Wait for every input to actually load before deciding, so the sheet never
  // flashes in on half-resolved data. The entry only needs to be "loaded" when we
  // actually queried it (a current round exists).
  const entryQueried = consider && !!leagueId && !!roundId && !!uid;
  const ready =
    consider &&
    profile !== undefined &&
    memberships !== undefined &&
    miniLeagues !== undefined &&
    (!entryQueried || entry !== undefined);

  const eligible =
    ready &&
    profile?.profileComplete !== false &&
    (memberships?.length ?? 0) > 0 &&
    !hasMiniLeague &&
    hasPicked;

  // Don't interrupt the picking flow, and don't preach to players already looking
  // at the leagues section (or the web admin).
  const routeOk =
    !pathname.includes('/picks') &&
    !pathname.startsWith('/leagues') &&
    !pathname.startsWith('/admin');

  useEffect(() => {
    if (openedRef.current || open) return;
    if (!eligible || !routeOk) return;
    const t = setTimeout(() => {
      openedRef.current = true;
      setOpen(true);
      // Mark seen the instant it shows: a promo nudges once, it never nags.
      setSeen(true);
      try {
        localStorage.setItem(SEEN_KEY, '1');
      } catch {
        /* private mode — fine, it just won't persist past this session */
      }
      // Record the impression on the account (the admin funnel's denominator).
      const shownAt = Date.now();
      shownAtRef.current = shownAt;
      updateUserRef.current.mutate({ miniLeaguePromo: { shownAt } });
    }, SHOW_DELAY_MS);
    return () => clearTimeout(t);
  }, [eligible, routeOk, open]);

  // Record which exit the player took (first one wins), reusing the impression's
  // shownAt so the stored state is one coherent { shownAt, outcome, outcomeAt }.
  const recordOutcome = (outcome: NonNullable<MiniLeaguePromoState['outcome']>) => {
    if (outcomeRef.current || !uid) return;
    outcomeRef.current = true;
    const shownAt = shownAtRef.current ?? profile?.miniLeaguePromo?.shownAt ?? Date.now();
    updateUser.mutate({ miniLeaguePromo: { shownAt, outcome, outcomeAt: Date.now() } });
  };

  const close = () => {
    recordOutcome('dismiss');
    setOpen(false);
  };

  const goCreate = () => {
    recordOutcome('create');
    impact('medium');
    setOpen(false);
    navigate(createMiniLeaguePath(leagueId ?? undefined));
  };

  const goJoin = () => {
    recordOutcome('join');
    impact('light');
    setOpen(false);
    navigate('/leagues/join');
  };

  return (
    <Sheet open={open} onClose={close} ariaLabel="Start a mini league" className="px-5 pb-7 pt-1">
      <MiniLeaguePromoContent
        leagueName={activeLeague?.name}
        onClose={close}
        onCreate={goCreate}
        onJoin={goJoin}
      />
    </Sheet>
  );
}

/**
 * The promo's visual content — everything inside the sheet panel. Split out from the
 * gating wrapper so the admin dashboard can render the exact same markup for preview
 * (real component, no drift). `leagueName` personalises the pitch ("…let your
 * Premier League predictions…"); the handlers wire the three exits.
 */
export function MiniLeaguePromoContent({
  leagueName,
  onClose,
  onCreate,
  onJoin
}: {
  leagueName?: string;
  onClose: () => void;
  onCreate: () => void;
  onJoin: () => void;
}) {
  const predictionsLabel = leagueName ? `${leagueName} predictions` : 'predictions';

  return (
    <>
      <button
        type="button"
        onClick={onClose}
        aria-label="Close"
        className="tap absolute right-3 top-2 grid h-[30px] w-[30px] place-items-center rounded-full bg-fill text-ink2"
      >
        <X width={13} height={13} strokeWidth={3} className="text-ink3" />
      </button>

      {/* Hero — the mates to beat */}
      <div className="mt-3 flex justify-center">
        <div className="flex items-center">
          {PILE.map((a, i) => (
            <div
              key={a.letter}
              style={{
                width: a.size,
                height: a.size,
                marginLeft: i === 0 ? 0 : -12,
                fontSize: Math.round(a.size * 0.34),
                backgroundImage: `linear-gradient(135deg, ${a.from}, ${a.to})`
              }}
              className="grid shrink-0 place-items-center rounded-full border-[3px] border-white font-extrabold text-white shadow-pill"
            >
              {a.letter}
            </div>
          ))}
          <div className="-ml-3 grid h-[46px] w-[46px] shrink-0 place-items-center rounded-full border-[3px] border-white bg-ink text-[13px] font-extrabold tracking-tight text-white">
            +4
          </div>
        </div>
      </div>

      {/* Headline */}
      <div className="mt-[18px] text-center">
        <h2 className="title text-[25px] leading-[1.08] text-ink">Think you&apos;d beat your mates?</h2>
        <p className="mx-auto mt-2.5 max-w-[300px] text-[14px] font-medium leading-[1.45] text-ink2">
          Start a private mini league, invite the group, and let your {predictionsLabel} settle who really knows the
          game.
        </p>
      </div>

      {/* Competitive teaser table — your empty hot seat */}
      <div className="mt-[18px] flex flex-col gap-0.5 rounded-[20px] bg-ink p-[5px]">
        <div className="flex items-center gap-2.5 px-3 py-[9px]">
          <span className="num w-3.5 shrink-0 text-center text-[12px] font-extrabold text-ink3">1</span>
          <span className="grid h-[26px] w-[26px] shrink-0 place-items-center rounded-full border-[1.5px] border-white/25 bg-gradient-to-br from-night2 to-night text-[11px] font-extrabold text-white">
            T
          </span>
          <span className="flex-1 truncate text-[13px] font-bold tracking-snug text-white">TopBins</span>
          <span className="num shrink-0 text-[13px] font-extrabold text-white">248</span>
        </div>

        <div className="relative flex items-center gap-2.5 overflow-hidden rounded-[15px] bg-gradient-to-br from-accent to-accent-deep px-3 py-[11px]">
          <span className="num w-3.5 shrink-0 text-center text-[12px] font-extrabold text-[#06371F]">?</span>
          <span className="grid h-[26px] w-[26px] shrink-0 place-items-center rounded-full border-[1.5px] border-dashed border-white/80 bg-white/[0.28]">
            <Plus width={14} height={14} strokeWidth={2.4} className="text-white" />
          </span>
          <span className="flex-1 truncate text-[13px] font-extrabold tracking-snug text-white">
            Your spot&apos;s waiting
          </span>
          <span className="shrink-0 rounded-md bg-white/85 px-[7px] py-[3px] text-[12px] font-extrabold text-[#06371F]">
            JOIN
          </span>
          <div
            aria-hidden
            className="pointer-events-none absolute inset-y-0 left-0 w-2/5 animate-h2h-sheen bg-gradient-to-r from-transparent via-white/35 to-transparent"
          />
        </div>
      </div>

      {/* Primary CTA */}
      <button
        type="button"
        onClick={onCreate}
        className="tap mt-[18px] flex w-full items-center justify-center gap-2 rounded-[16px] bg-gradient-to-br from-accent to-accent-deep py-4 shadow-featured"
      >
        <span className="text-[16px] font-extrabold tracking-snug text-white">Create a Mini League</span>
        <ArrowRight width={18} height={18} strokeWidth={2.6} className="text-white" />
      </button>

      {/* Secondary — join with a code */}
      <div className="mt-[11px] flex items-center justify-center gap-[7px]">
        <span className="text-[13px] font-semibold text-ink2">Got a code?</span>
        <button
          type="button"
          onClick={onJoin}
          className="tap text-[13px] font-extrabold tracking-snug text-accent-ink"
        >
          Join a league
        </button>
      </div>
    </>
  );
}
